import React, { useCallback, useEffect, useState } from 'react';
import ConversationChat from 'features/agent-analytics/components/ConversationChat';
import { getConversation } from 'features/agent-analytics/api/conversationsApi';
import { truncate } from 'features/agent-analytics/components/runCells';

/**
 * Conversations tab of the run drawer — the conversation this run belongs to,
 * rendered as a chat transcript with the current run highlighted. Clicking any
 * other exchange swaps the drawer to that run.
 *
 * This used to be a numbered list (`#1 agent ok 12:04:02 1.2s`), which told you
 * how many runs there were but nothing about what was said, so the only way to
 * follow a conversation was to open each run in turn and read its Activity tab.
 *
 * It fetches its own data rather than reusing the `conversationRuns` the run
 * detail response already carries: that sibling query deliberately omits
 * `input`/`output`, and widening it would put two large payloads per sibling on
 * the critical path of every single run open, transcript or not.
 */
export default function ConversationRunsPanel({
  teamId,
  conversationId,
  /** The open run's start time — anchors which sitting of the thread to show. */
  around,
  activeRunId,
  onSelect,
  /** Sibling rows from the run detail response — used for an instant count
   *  while the transcript loads. */
  fallbackRuns = [],
}) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!conversationId || !teamId) { setRuns([]); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await getConversation({ teamId, conversationId, around });
      setRuns(data.runs);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load conversation.');
      setRuns([]);
    } finally {
      setLoading(false);
    }
  }, [conversationId, teamId, around]);

  useEffect(() => { load(); }, [load]);

  if (!conversationId) {
    return (
      <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-[color:var(--border-default)] bg-surface text-[12px] text-[color:var(--text-muted)]">
        This run isn&apos;t part of a conversation.
      </div>
    );
  }

  const count = runs.length || fallbackRuns.length || 0;

  return (
    <section className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2 px-1 pb-1">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">
          {count} run{count === 1 ? '' : 's'} in this session
        </p>
        <span className="font-mono text-[11px] text-[color:var(--text-secondary)]" title={String(conversationId)}>
          {truncate(String(conversationId), 32)}
        </span>
      </div>
      <div className="-mx-1">
        <ConversationChat
          runs={runs}
          loading={loading}
          error={error}
          activeRunId={activeRunId}
          onOpenRun={onSelect}
          emptyMessage="No runs in this conversation."
        />
      </div>
    </section>
  );
}
